import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApplicationByApplicantGuid, getApplicationByJobId, initializeAnswersForInterview } from '../api/ApplicationService';
import { getAnswersByApplicantGuid } from '../api/AnswerService';
import { FormBuilderService } from '../api/FormBuilderService';
import { MediaService } from '../api/MediaService';
import { clearAllItems, setWithExpiry } from '../utils/sessionStorage';
import { useInternetConnection } from '../utils/connection';
import { setStatsigUser } from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getScreenerSource } from '../utils/getScreenerSource';

interface UseOnboardingDataReturn {
  application: any;
  job: any;
  isLoading: boolean;
  error: string | null;
  hasQuestionnaire: boolean;
  contentType: string | null;
  ttsLoading: boolean;
  isConnected: boolean;
  interviewId: string | null;
  questionCount: number;
  // VI-594 resume support: number of questions already answered for this
  // applicant on a previous session. 0 means fresh start.
  answeredCount: number;
  // True when the candidate has partial progress (status Pending and at
  // least one answer submitted). Drives the Resume Interview modal.
  hasPartialProgress: boolean;
  setError: (error: string | null) => void;
}

export const useOnboardingData = (applicantGuid: string | undefined): UseOnboardingDataReturn => {
  const navigate = useNavigate();
  const isConnected = useInternetConnection();

  const [application, setApplication] = useState<any>(null);
  const [job, setJob] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [hasQuestionnaire, setHasQuestionnaire] = useState(true);
  const [contentType, setContentType] = useState<string | null>(null);
  const [ttsLoading, setTtsLoading] = useState(true);
  const [questionCount, setQuestionCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [hasPartialProgress, setHasPartialProgress] = useState(false);

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (!applicantGuid) return;

      setIsLoading(true);
      setError(null);

      try {
        const applicationResponse = await getApplicationByApplicantGuid(applicantGuid);
        if (applicationResponse && applicationResponse.length > 0) {
          const applicationData = applicationResponse[0];
          if (applicationData.status === 'Completed') {
            console.warn('Interview already completed');
            navigate('/v1/interview/already-completed');
            return;
          }

          // VI-594 resume support: fetch any existing answers BEFORE the
          // clearAllItems() below wipes sessionStorage. If Reconciled
          // upstream, treat it the same as a Completed interview record.
          // Otherwise capture how many questions were already answered so
          // the landing page can offer Continue / Restart.
          const answersDoc = await getAnswersByApplicantGuid(applicantGuid);
          if (answersDoc?.status === 'Reconciled') {
            console.warn('Answers already reconciled upstream');
            navigate('/v1/interview/already-completed');
            return;
          }
          const previousAnswerCount = answersDoc?.answers?.length ?? 0;

          clearAllItems();

          setWithExpiry('applicantGuid', applicationData.applicationGuid, 1000 * 60 * 60 * 24);
          // VI-834 / VI-846: persist the applicant's first name so the redesigned
          // recording pages can greet them ("Hi Sam! Welcome to your interview…").
          // The interview endpoint returns it as `firstname` (lowercase, per the
          // JobApplicationAPI/Swagger schema); accept the camelCase variant too.
          // Optional — the pages fall back to a name-less greeting when it's absent.
          const applicantFirstName = applicationData.firstName ?? applicationData.firstname ?? '';
          if (applicantFirstName) {
            setWithExpiry('applicantFirstName', applicantFirstName, 1000 * 60 * 60 * 24);
          }
          initializeAnswersForInterview(applicationData.applicationGuid);
          setWithExpiry('jobId', applicationData.jobId, 1000 * 60 * 60 * 24);
          setWithExpiry('interviewId', applicationData._id, 1000 * 60 * 60 * 24);
          setInterviewId(applicationData._id);

          if (applicationData.jobId) {
            const [jobResponse, questionnaireResponse] = await Promise.all([
              getApplicationByJobId(applicationData.jobId),
              FormBuilderService.checkJobQuestionnaire(applicationData.jobId),
            ]);

            if (!jobResponse) {
              console.error('Failed to fetch job data for jobId:', applicationData.jobId);
              setError(`Failed to fetch job information. Please try again later. (${Date.now()})`);
              return;
            }

            if (!jobResponse.companyId) {
              console.error('Job response missing companyId:', jobResponse);
              setError(`Job information is incomplete. Please contact support. (${Date.now()})`);
              return;
            }

            const companyIdString = jobResponse.companyId.toString();
            setWithExpiry('companyId', companyIdString, 1000 * 60 * 60 * 24);
            setWithExpiry('job', jobResponse, 1000 * 60 * 60 * 24);
            setJob(jobResponse);

            void setStatsigUser({
              userID: applicationData.applicationGuid,
              custom: {
                jobId: applicationData.jobId?.toString() ?? '',
                companyId: companyIdString,
                applicantGuid: applicationData.applicationGuid,
                deviceType: getDeviceType(),
                source: getScreenerSource(),
              },
            });

            if (questionnaireResponse.hasQuestionnaire === true && questionnaireResponse.data.active === true) {
              const questionnaires = questionnaireResponse.data.questions;
              setQuestionCount(questionnaires.length);
              setContentType(questionnaireResponse.data.contentType.toString().toLowerCase());
              setWithExpiry('questionnaires', questionnaires, 1000 * 60 * 60 * 24);

              // VI-594: derive resume state. Clamp against total in case the
              // questionnaire shrank since the last session; only treat as
              // partial progress when there's something to resume AND the
              // candidate hasn't somehow exceeded the question count.
              const clampedAnswered = Math.min(
                previousAnswerCount,
                questionnaires.length,
              );
              const willShowResumeModal =
                clampedAnswered > 0 && clampedAnswered < questionnaires.length;
              setAnsweredCount(clampedAnswered);
              setHasPartialProgress(willShowResumeModal);

              setIsLoading(false);

              const questionTexts = questionnaires.map((question: any) => question.questionText);
              const ttsAudio = await MediaService.generateTTSAudio(questionTexts, applicationData.applicationGuid);
              setTtsLoading(false);
              setWithExpiry('ttsAudio', ttsAudio.data.audioFilePaths, 1000 * 60 * 60 * 24);

              axios.patch(`/api/v1/interview/${applicantGuid}`, { status: 'Incomplete' });
            } else {
              setHasQuestionnaire(false);
            }
          }
        } else {
          console.error('No application found for this applicant');
          setError(`No application found for this applicant. (${Date.now()})`);
        }

        setApplication(applicationResponse);
      } catch (err: unknown) {
        setError(`There was an error fetching the application data. Please try again later. (${Date.now()})`);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationData();
  }, [applicantGuid, navigate]);

  return {
    application,
    job,
    isLoading,
    error,
    hasQuestionnaire,
    contentType,
    ttsLoading,
    isConnected,
    interviewId,
    questionCount,
    answeredCount,
    hasPartialProgress,
    setError,
  };
};
